import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Alert, Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";

import {
  getPublicBookingNextSlot,
  PublicBookingNextSlotData,
} from "../../services/publicBookingService";

interface InstagramBioLinkCardProps {
  bookingToken: string | null | undefined;
}

const InstagramBioLinkCard: React.FC<InstagramBioLinkCardProps> = ({ bookingToken }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nextSlot, setNextSlot] = useState<PublicBookingNextSlotData | null>(null);

  const loadNextSlot = useCallback(async (): Promise<void> => {
    if (!bookingToken) {
      setNextSlot(null);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const response = await getPublicBookingNextSlot(bookingToken);
      setNextSlot(response.data ?? null);
    } catch (error: unknown) {
      setNextSlot(null);
      const message =
        typeof error === "object" &&
        error !== null &&
        "data" in error &&
        typeof (error as { data?: unknown }).data === "string"
          ? (error as { data: string }).data
          : t("settings.bio_link.errors.loadFailed");
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [bookingToken, t]);

  useEffect(() => {
    void loadNextSlot();
  }, [loadNextSlot]);

  const copyToClipboard = async (value: string, toastKey: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t(toastKey));
    } catch {
      toast.error(t("settings.bio_link.toast.copyError"));
    }
  };

  const hasSlot = nextSlot?.slot !== null && nextSlot?.slot !== undefined;
  const previewCopy = nextSlot?.copy?.trim() ?? "";
  const bookUrl = nextSlot?.book_url?.trim() ?? "";

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center gap-2">
        <i className="ri-instagram-line fs-20 text-danger" aria-hidden="true" />
        <h5 className="card-title mb-0">{t("settings.bio_link.title")}</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted small mb-3">{t("settings.bio_link.description")}</p>

        {!bookingToken ? (
          <Alert color="warning" className="mb-0">
            {t("settings.bio_link.errors.tokenUnavailable")}
          </Alert>
        ) : loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" size="sm" />
          </div>
        ) : loadError ? (
          <Alert color="danger" className="mb-0">
            {loadError}
          </Alert>
        ) : (
          <>
            <div
              className={`border rounded-3 p-3 mb-3 ${
                hasSlot ? "bg-light border-success border-opacity-25" : "bg-warning-subtle border-warning"
              }`}
            >
              {hasSlot ? (
                <>
                  <p className="text-muted small mb-2">{t("settings.bio_link.preview.availableLabel")}</p>
                  <p className="fw-semibold mb-1">{previewCopy}</p>
                  {nextSlot?.slot?.local_label ? (
                    <p className="text-muted small mb-0">{nextSlot.slot.local_label}</p>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="text-muted small mb-2">{t("settings.bio_link.preview.emptyLabel")}</p>
                  <p className="mb-0">{previewCopy || t("settings.bio_link.preview.emptyFallback")}</p>
                </>
              )}
            </div>

            <div className="d-flex flex-wrap gap-2">
              <Button
                color="soft-primary"
                size="sm"
                disabled={!previewCopy}
                onClick={() => void copyToClipboard(previewCopy, "settings.bio_link.toast.copyTextSuccess")}
              >
                <i className="ri-file-copy-line align-bottom me-1" aria-hidden="true" />
                {t("settings.bio_link.actions.copyText")}
              </Button>
              <Button
                color="soft-success"
                size="sm"
                disabled={!bookUrl}
                onClick={() => void copyToClipboard(bookUrl, "settings.bio_link.toast.copyLinkSuccess")}
              >
                <i className="ri-link align-bottom me-1" aria-hidden="true" />
                {t("settings.bio_link.actions.copyLink")}
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default InstagramBioLinkCard;
